import { useState } from 'react'
import { Box, Flex, HStack, VStack } from '@chakra-ui/react'
import { useNavigate } from 'react-router-dom'
import { LuSiren, LuTriangleAlert, LuClock, LuMapPin } from 'react-icons/lu'
import { DataTable, DataList, PageHeader, Text, useT } from '@minetrace/ui'
import { brand, themeColors } from '../../../theme'
import { plantGateService } from '../../../api/services/plantGate.service'
import { usePagedRequest } from '../../../hooks'
import type {
  PlantGateEtaState,
  PlantGateListItemResponse,
} from '../../../api/models/plantGate.models'
import { parseUtcDate } from '../../../lib/dates'

const ETA_COLOR: Record<PlantGateEtaState, string> = {
  EnRoute: '#2a8a87',
  AtGate: '#d49a2a',
  Stopped: '#3d6fa8',
  Stale: '#b5aa9d',
  Unknown: '#857b71',
  Finished: '#b4532a',
}

function formatLastSeen(iso: string | null) {
  if (!iso) return '—'
  const d = parseUtcDate(iso)
  const diffMin = Math.round((Date.now() - d.getTime()) / 60000)
  if (diffMin < 1) return 'hace <1 min'
  if (diffMin < 60) return `hace ${diffMin} min`
  const h = Math.floor(diffMin / 60)
  if (h < 24) return `hace ${h}h`
  const days = Math.floor(h / 24)
  return `hace ${days}d`
}

function formatMinutes(min: number) {
  if (min < 60) return `${min} min`
  const h = Math.floor(min / 60)
  const m = min % 60
  return m === 0 ? `${h}h` : `${h}h ${m}m`
}

function formatEta(eta: PlantGateListItemResponse) {
  if (eta.etaState === 'AtGate') return 'En garita'
  if (eta.etaState === 'Finished') return 'Llegó'
  if (eta.etaState === 'Unknown') return '—'
  if (eta.etaMinutes == null) return eta.etaState === 'Stale' ? 'Señal antigua' : '—'
  // Stopped: prefix with "≈" so it reads as the projected-once-it-moves time.
  const time = formatMinutes(eta.etaMinutes)
  if (eta.etaState === 'Stopped') return `≈ ${time}`
  if (eta.etaState === 'Stale') return `≈ ${time}`
  return time
}

export default function ControlIngresoPage() {
  const colors = themeColors
  const navigate = useNavigate()
  const { t } = useT()
  const [search, setSearch] = useState('')

  const {
    items,
    totalCount,
    isFetching: loading,
    error,
    page,
    setPage,
    totalPages,
    pageSize,
    setPageSize,
  } = usePagedRequest(plantGateService.getAll, {
    params: { search: search.trim() || undefined },
  })

  return (
    <VStack gap={5} alignItems="stretch">
      <Flex justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={3}>
        <PageHeader
          title={t('plantGate.title', { defaultValue: 'Control de Ingreso' })}
          subtitle={t('plantGate.subtitle', {
            defaultValue:
              'Traslados en camino o ya finalizados sin recepción. Anticipa alertas antes de que llegue el camión.',
          })}
          colors={colors}
        />
      </Flex>

      <Box
        p={3}
        borderRadius="md"
        bg="#3d6fa811"
        borderWidth="1px"
        borderColor="#3d6fa833"
        color={colors.text}
        fontSize="xs"
        lineHeight="1.5"
      >
        <strong>Pre-validación:</strong> los conteos de alertas se calculan sobre datos almacenados en
        el sistema, no contra servicios externos (SUNAT, IGAFOM, etc.). La validación oficial se hace
        al recibir el camión con la app móvil y puede arrojar alertas adicionales.
      </Box>

      <DataList<PlantGateListItemResponse>
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder={t('plantGate.searchPlaceholder', {
          defaultValue: 'Buscar por lote, RUC, proveedor, placa o conductor',
        })}
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        totalCount={totalCount}
        entityName={t('plantGate.entityName', { defaultValue: 'traslados' })}
        pageSize={pageSize}
        onPageSizeChange={setPageSize}
        brandColor={brand.green}
        headers={[
          t('plantGate.headers.batchCode', { defaultValue: 'Lote' }),
          t('plantGate.headers.supplier', { defaultValue: 'Proveedor' }),
          t('plantGate.headers.miningRight', { defaultValue: 'Derecho minero' }),
          t('plantGate.headers.transport', { defaultValue: 'Vehículo / Conductor' }),
          t('plantGate.headers.lastSeen', { defaultValue: 'Última señal' }),
          t('plantGate.headers.eta', { defaultValue: 'ETA' }),
          t('plantGate.headers.alertasGraves', { defaultValue: 'Alertas graves' }),
          t('plantGate.headers.observaciones', { defaultValue: 'Observaciones' }),
        ]}
        data={items}
        loading={loading}
        error={error}
        emptyMessage={t('plantGate.emptyMessage', {
          defaultValue: 'No hay traslados en camino esperando recepción',
        })}
        loadingMessage={t('plantGate.loadingMessage', { defaultValue: 'Cargando…' })}
        colors={colors}
        keyExtractor={(r) => r.id}
        renderRow={(row) => {
          const etaColor = ETA_COLOR[row.etaState]
          return (
            <>
              {/* Lote */}
              <DataTable.Cell>
                <Text
                  fontFamily="'JetBrains Mono', monospace"
                  fontSize="sm"
                  fontWeight="600"
                  color={colors.text}
                >
                  {row.batchCode}
                </Text>
                <Text fontSize="xs" color={colors.textMuted}>
                  {row.shipmentStateName}
                </Text>
              </DataTable.Cell>

              {/* Proveedor */}
              <DataTable.Cell>
                <VStack gap={0} alignItems="flex-start">
                  <Text fontSize="sm" color={colors.text} wordBreak="break-word">
                    {row.supplierName ?? '—'}
                  </Text>
                  <Text fontSize="xs" color={colors.textMuted}>
                    {row.supplierRuc ?? '—'}
                  </Text>
                </VStack>
              </DataTable.Cell>

              {/* Derecho minero */}
              <DataTable.Cell>
                <VStack gap={0} alignItems="flex-start">
                  <Text fontSize="sm" color={colors.text} wordBreak="break-word">
                    {row.miningRightName ?? '—'}
                  </Text>
                  {row.mineralTypeName && (
                    <Text fontSize="xs" color={brand.green} fontWeight="500">
                      {row.mineralTypeName}
                    </Text>
                  )}
                </VStack>
              </DataTable.Cell>

              {/* Vehículo / Conductor */}
              <DataTable.Cell>
                <VStack gap={0} alignItems="flex-start">
                  <Text
                    fontFamily="'JetBrains Mono', monospace"
                    fontSize="sm"
                    fontWeight="600"
                    color={colors.text}
                  >
                    {row.vehiclePlate ?? '—'}
                  </Text>
                  <Text fontSize="xs" color={colors.textMuted}>
                    {row.driverName ?? '—'}
                  </Text>
                </VStack>
              </DataTable.Cell>

              {/* Última señal */}
              <DataTable.Cell>
                <HStack gap={1.5}>
                  <Box color={colors.textMuted}>
                    <LuClock size={12} />
                  </Box>
                  <Text fontSize="xs" color={colors.textMuted} whiteSpace="nowrap">
                    {formatLastSeen(row.lastPointAt)}
                  </Text>
                </HStack>
              </DataTable.Cell>

              {/* ETA */}
              <DataTable.Cell>
                <Box
                  display="inline-flex"
                  alignItems="center"
                  gap={1.5}
                  px={2.5}
                  py={1}
                  borderRadius="full"
                  fontSize="11px"
                  fontWeight="700"
                  bg={`${etaColor}22`}
                  color={etaColor}
                  whiteSpace="nowrap"
                >
                  <LuMapPin size={11} />
                  {formatEta(row)}
                </Box>
              </DataTable.Cell>

              {/* Alertas graves */}
              <DataTable.Cell>
                {row.alertasGravesCount > 0 ? (
                  <Box
                    display="inline-flex"
                    alignItems="center"
                    gap={1.5}
                    px={2.5}
                    py={1}
                    borderRadius="full"
                    fontSize="11px"
                    fontWeight="700"
                    bg="#c8442f22"
                    color="#c8442f"
                  >
                    <LuSiren size={12} />
                    {row.alertasGravesCount}
                  </Box>
                ) : (
                  <Text fontSize="xs" color={colors.textMuted}>
                    —
                  </Text>
                )}
              </DataTable.Cell>

              {/* Observaciones */}
              <DataTable.Cell>
                {row.observacionesCount > 0 ? (
                  <Box
                    display="inline-flex"
                    alignItems="center"
                    gap={1.5}
                    px={2.5}
                    py={1}
                    borderRadius="full"
                    fontSize="11px"
                    fontWeight="700"
                    bg="#EAB30822"
                    color="#8a5f0b"
                  >
                    <LuTriangleAlert size={12} />
                    {row.observacionesCount}
                  </Box>
                ) : (
                  <Text fontSize="xs" color={colors.textMuted}>
                    —
                  </Text>
                )}
              </DataTable.Cell>
            </>
          )
        }}
        actions={(row) => [
          {
            label: t('plantGate.actions.viewDetail', { defaultValue: 'Ver detalle' }),
            onClick: () => navigate(`/app/planta/garita/${row.id}`),
          },
        ]}
      />
    </VStack>
  )
}
